import { useState } from "react";
import { Navigate } from "react-router-dom";
import { Bot, ShieldCheck, MessageCircle } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function Login() {
  const { user, setUser } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  if (user) return <Navigate to={user.role === "superadmin" ? "/admin" : "/"} replace />;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      setUser(data);
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-[1.1fr_1fr] bg-background">
      <div className="hidden lg:flex flex-col justify-between p-14 bg-[#06382B] relative overflow-hidden">
        <img src="/login-hero.jpg" alt="" className="absolute inset-0 w-full h-full object-cover opacity-25" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#06382B] via-[#06382B]/80 to-transparent" />
        <div className="relative flex items-center gap-3">
          <div className="h-11 w-11 rounded-xl bg-[#D4AF37] grid place-items-center text-[#073B2B] font-heading font-extrabold text-xl">I</div>
          <span className="font-heading text-2xl font-bold text-white">ImobiAI</span>
        </div>
        <div className="relative max-w-lg">
          <h1 className="font-heading text-5xl font-bold text-white leading-tight tracking-tight">Atendimento imobiliário <span className="text-[#D4AF37]">inteligente</span> no WhatsApp.</h1>
          <p className="text-emerald-100/80 mt-6 text-lg">IA de pré-atendimento, transferência para corretores e CRM completo — tudo no número oficial da sua imobiliária.</p>
          <div className="mt-10 grid grid-cols-3 gap-4 text-emerald-50/90 text-sm">
            <div className="flex items-center gap-2"><Bot className="h-4 w-4 text-[#D4AF37]" />IA configurável</div>
            <div className="flex items-center gap-2"><MessageCircle className="h-4 w-4 text-[#D4AF37]" />API oficial Meta</div>
            <div className="flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-[#D4AF37]" />LGPD</div>
          </div>
        </div>
        <div className="relative text-xs text-emerald-100/50">© {new Date().getFullYear()} ImobiAI</div>
      </div>
      <div className="flex items-center justify-center p-6 sm:p-12">
        <form onSubmit={submit} className="w-full max-w-sm space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500" data-testid="login-form">
          <div>
            <h2 className="font-heading text-3xl font-bold tracking-tight">Entrar</h2>
            <p className="text-muted-foreground text-sm mt-2">Acesse o painel da sua imobiliária ou a administração da plataforma.</p>
          </div>
          <div className="space-y-2">
            <Label htmlFor="email">E-mail</Label>
            <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="login-email-input" autoComplete="email" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="password">Senha</Label>
            <Input id="password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} data-testid="login-password-input" autoComplete="current-password" />
          </div>
          {error && <div className="text-sm text-destructive bg-destructive/10 rounded-md px-3 py-2" data-testid="login-error">{error}</div>}
          <Button type="submit" disabled={loading} className="w-full h-11 bg-primary hover:bg-primary/90" data-testid="login-submit-button">{loading ? "Entrando..." : "Entrar"}</Button>
          <p className="text-xs text-muted-foreground text-center">Acesso liberado pelo administrador da plataforma.</p>
        </form>
      </div>
    </div>
  );
}
